import { observer, useLocalObservable } from 'mobx-react';
import { Fragment, useEffect, useRef } from 'react';
import { observable, runInAction } from 'mobx';
import { Oval } from 'react-loader-spinner';
import { ICON_BUTTON_ICONS } from '../../../../components/buttons/IconButton';

// Shared progress phases for both fresh-start and resume paths.
const PROGRESS_PHASES = [
  { timeStart: 0, timeEnd: 0.15, startProgress: 0, endProgress: 15, increment: 1, delay: 150 },
  { timeStart: 0.15, timeEnd: 0.18, startProgress: 15, endProgress: 25, increment: 10, delay: 300 },
  { timeStart: 0.18, timeEnd: 0.35, startProgress: 25, endProgress: 45, increment: 2, delay: 200 },
  { timeStart: 0.35, timeEnd: 0.4, startProgress: 45, endProgress: 58, increment: 13, delay: 400 },
  { timeStart: 0.4, timeEnd: 0.65, startProgress: 58, endProgress: 76, increment: 2, delay: 180 },
  { timeStart: 0.65, timeEnd: 0.7, startProgress: 76, endProgress: 85, increment: 9, delay: 350 },
  { timeStart: 0.7, timeEnd: 1.0, startProgress: 85, endProgress: 98, increment: 1, delay: 250 }
];

// External observable for state persistence
const savedState = observable({
  savedCalculations: {},
  saveCalculations: (searchId, calculations) => {
    savedState.savedCalculations[searchId] = calculations;
  },
  getCalculations: (searchId) => {
    return savedState.savedCalculations[searchId];
  },
  hasCalculations: (searchId) => {
    return !!savedState.savedCalculations[searchId];
  }
});

// Hook that handles all the logic and returns props for the component
export const useSearchResultsLoader = ({
  searchId,
  criteria = [],
  totalPrintTime = 55_000,
  withRationale = false,
  isComparables = false,
  start = false,
  keyRender = null,
  disableAutoScroll = false,
  searchProgress = null // Event-driven progress (null = timer mode, non-null = event mode)
}) => {
  const state = useLocalObservable(() => ({
    localSearchId: searchId,
    localCriteria: criteria,
    localTotalPrintTime: totalPrintTime,
    localWithRationale: withRationale,
    localIsComparables: isComparables,
    localCurrentStep: 'none',
    localLoadingProgress: 0,
    localPrintedCriteriaTitle: '',
    localPrintedCriteriaContent: [],
    localCurrentSection: -1,
    localPrintedSectionTitles: {},
    localPrintedSectionContent: {},
    localWithRationaleState: withRationale,
    localAnimationStarted: false,

    get currentStep() {
      if (state.localSearchId && savedState.hasCalculations(state.localSearchId)) {
        return savedState.getCalculations(state.localSearchId).currentStep || 'none';
      }
      return state.localCurrentStep;
    },

    get loadingProgress() {
      if (state.localSearchId && savedState.hasCalculations(state.localSearchId)) {
        return savedState.getCalculations(state.localSearchId).loadingProgress || 0;
      }
      return state.localLoadingProgress;
    },

    get printedCriteriaTitle() {
      if (state.localSearchId && savedState.hasCalculations(state.localSearchId)) {
        return savedState.getCalculations(state.localSearchId).printedCriteriaTitle || '';
      }
      return state.localPrintedCriteriaTitle;
    },

    get printedCriteriaContent() {
      if (state.localSearchId && savedState.hasCalculations(state.localSearchId)) {
        return savedState.getCalculations(state.localSearchId).printedCriteriaContent || [];
      }
      return state.localPrintedCriteriaContent;
    },

    get currentSection() {
      if (state.localSearchId && savedState.hasCalculations(state.localSearchId)) {
        const saved = savedState.getCalculations(state.localSearchId);
        return saved.currentSection !== undefined ? saved.currentSection : -1;
      }
      return state.localCurrentSection;
    },

    get printedSectionTitles() {
      if (state.localSearchId && savedState.hasCalculations(state.localSearchId)) {
        return savedState.getCalculations(state.localSearchId).printedSectionTitles || {};
      }
      return state.localPrintedSectionTitles;
    },

    get printedSectionContent() {
      if (state.localSearchId && savedState.hasCalculations(state.localSearchId)) {
        return savedState.getCalculations(state.localSearchId).printedSectionContent || {};
      }
      return state.localPrintedSectionContent;
    },

    get withRationaleState() {
      if (state.localSearchId && savedState.hasCalculations(state.localSearchId)) {
        return savedState.getCalculations(state.localSearchId).withRationaleState !== undefined
          ? savedState.getCalculations(state.localSearchId).withRationaleState
          : state.localWithRationale;
      }
      return state.localWithRationaleState;
    },

    get animationStarted() {
      if (state.localSearchId && savedState.hasCalculations(state.localSearchId)) {
        return savedState.getCalculations(state.localSearchId).animationStarted || false;
      }
      return state.localAnimationStarted;
    },

    get allSections() {
      const sections = [];

      if (state.localCriteria.length > 0) {
        sections.push({
          id: 'criteria',
          title: state.localIsComparables
            ? 'Checking for comparable companies with the following characteristics:'
            : "You're searching for companies with the following characteristics:",
          icon: ICON_BUTTON_ICONS.SEARCH,
          content: state.localCriteria.map(({ name, display }) => `${name}: ${display}`).join('\n'),
          isCriteria: true
        });
      }

      const loadingSections = [
        {
          id: 'initiating',
          title: state.localIsComparables
            ? 'Initiating comparable companies search…'
            : 'Initiating search…',
          icon: ICON_BUTTON_ICONS.ROCKET,
          content: [
            'Connecting to ',
            {
              type: 'img',
              props: {
                src: '/images/header-logo-desktop-new.svg',
                className: 'transact-logo-text-sm'
              }
            },
            "'s comprehensive private market database.",
            { type: 'br' },
            'Launching data retrieval across 500+ million company data points.'
          ]
        },
        {
          id: 'analyzing',
          title: 'Analyzing company data',
          icon: ICON_BUTTON_ICONS.FUNNEL,
          content:
            'Performing multi-criteria analysis on matching companies...\nEvaluating industry alignment, value chain positioning, and product portfolio relevance...'
        },
        {
          id: 'evaluating',
          title: 'Evaluating relevant companies',
          icon: ICON_BUTTON_ICONS.GRAPH_UP,
          content:
            'Evaluating companies for strategic relevance and data completeness...\nPrioritizing firms with sufficient financial disclosures'
        }
      ];

      if (state.withRationaleState) {
        loadingSections.push({
          id: 'rationale',
          title: 'Generating rationale',
          icon: ICON_BUTTON_ICONS.BULLSEYE,
          content:
            'Generating rationale for each shortlisted company…\nPreparing concise explanatory notes describing selection criteria.'
        });
      }

      loadingSections.push({
        id: 'preparing',
        title: 'Preparing results',
        icon: ICON_BUTTON_ICONS.TOOLS,
        content:
          'Compiling final list of companies…\nFormatting profiles and keyword highlights.\nAlmost ready – get ready to explore your results.'
      });

      sections.push(...loadingSections);
      return sections;
    },

    updateCurrentStep: function (value) {
      if (state.localSearchId) {
        state.updateSavedState({ currentStep: value });
      } else {
        state.localCurrentStep = value;
      }
    },

    updateLoadingProgress: function (value) {
      if (state.localSearchId) {
        state.updateSavedState({ loadingProgress: value });
      } else {
        state.localLoadingProgress = value;
      }
    },

    updatePrintedCriteriaTitle: function (value) {
      if (state.localSearchId) {
        state.updateSavedState({ printedCriteriaTitle: value });
      } else {
        state.localPrintedCriteriaTitle = value;
      }
    },

    updatePrintedCriteriaContent: function (value) {
      if (state.localSearchId) {
        state.updateSavedState({ printedCriteriaContent: value });
      } else {
        state.localPrintedCriteriaContent = value;
      }
    },

    updateCurrentSection: function (value) {
      if (state.localSearchId) {
        state.updateSavedState({ currentSection: value });
      } else {
        state.localCurrentSection = value;
      }
    },

    updatePrintedSectionTitles: function (index, value) {
      if (state.localSearchId) {
        const current = savedState.getCalculations(state.localSearchId) || {};
        const titles = { ...(current.printedSectionTitles || {}) };
        titles[index] = value;
        state.updateSavedState({ printedSectionTitles: titles });
      } else {
        state.localPrintedSectionTitles[index] = value;
      }
    },

    updatePrintedSectionContent: function (index, value) {
      if (state.localSearchId) {
        const current = savedState.getCalculations(state.localSearchId) || {};
        const content = { ...(current.printedSectionContent || {}) };
        content[index] = value;
        state.updateSavedState({ printedSectionContent: content });
      } else {
        state.localPrintedSectionContent[index] = value;
      }
    },

    updateAnimationStarted: function (value) {
      if (state.localSearchId) {
        state.updateSavedState({ animationStarted: value });
      } else {
        state.localAnimationStarted = value;
      }
    },

    updateSavedState: function (updates) {
      if (!state.localSearchId) return;

      const current = savedState.getCalculations(state.localSearchId) || {};
      const newState = {
        ...current,
        ...updates,
        withRationaleState: state.localWithRationaleState
      };

      savedState.saveCalculations(state.localSearchId, newState);
    },

    resetState: function () {
      if (state.localSearchId) {
        savedState.saveCalculations(state.localSearchId, {
          currentStep: 'none',
          loadingProgress: 0,
          printedCriteriaTitle: '',
          printedCriteriaContent: [],
          currentSection: -1,
          printedSectionTitles: {},
          printedSectionContent: {},
          withRationaleState: state.localWithRationale,
          animationStarted: false
        });
      } else {
        state.localCurrentStep = 'none';
        state.localLoadingProgress = 0;
        state.localPrintedCriteriaTitle = '';
        state.localPrintedCriteriaContent = [];
        state.localCurrentSection = -1;
        state.localPrintedSectionTitles = {};
        state.localPrintedSectionContent = {};
        state.localWithRationaleState = state.localWithRationale;
        state.localAnimationStarted = false;
      }
    }
  }));

  const intervalRef = useRef(null);
  const timeoutRefs = useRef([]);
  const isAnimatingRef = useRef(false);
  const isMountedRef = useRef(true);
  const creepIntervalRef = useRef(null);

  // Cleanup: Set isMountedRef to false on unmount to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearAllAnimations = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (creepIntervalRef.current) {
      clearInterval(creepIntervalRef.current);
      creepIntervalRef.current = null;
    }
    timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
    timeoutRefs.current = [];
    isAnimatingRef.current = false;
  };

  const typeText = (text, onUpdate, onComplete, duration = 2000) => {
    if (isAnimatingRef.current === false || !isMountedRef.current) return;

    if (
      !text ||
      (typeof text === 'string' && text.length === 0) ||
      (Array.isArray(text) && text.length === 0)
    ) {
      if (onComplete) onComplete();
      return;
    }

    if (Array.isArray(text)) {
      const textLength = text.reduce((acc, item) => {
        return acc + (typeof item === 'string' ? item.length : 0);
      }, 0);

      if (textLength === 0) {
        if (onComplete) onComplete();
        return;
      }

      const charDelay = Math.max(16, duration / textLength);
      let elementIndex = 0;
      let charIndexInElement = 0;
      let builtContent = [];

      const interval = setInterval(() => {
        if (isAnimatingRef.current === false || !isMountedRef.current) {
          clearInterval(interval);
          return;
        }

        if (elementIndex >= text.length) {
          clearInterval(interval);
          if (onComplete && isMountedRef.current) onComplete();
          return;
        }

        const currentElement = text[elementIndex];

        if (typeof currentElement === 'string') {
          if (charIndexInElement < currentElement.length) {
            const updatedContent = [...builtContent];
            for (let i = 0; i < elementIndex; i++) {
              if (updatedContent[i] === undefined) {
                updatedContent[i] = typeof text[i] === 'string' ? text[i] : text[i];
              }
            }
            updatedContent[elementIndex] = currentElement.substring(0, charIndexInElement + 1);
            if (isMountedRef.current) onUpdate(updatedContent);
            charIndexInElement++;
          } else {
            builtContent[elementIndex] = currentElement;
            elementIndex++;
            charIndexInElement = 0;
          }
        } else {
          builtContent[elementIndex] = currentElement;
          elementIndex++;
          charIndexInElement = 0;
        }
      }, charDelay);

      timeoutRefs.current.push(interval);
      return interval;
    } else {
      if (text.length === 0) {
        if (onComplete) onComplete();
        return;
      }

      let currentIndex = 0;
      const charDelay = Math.max(16, duration / text.length);

      const interval = setInterval(() => {
        if (isAnimatingRef.current === false || !isMountedRef.current) {
          clearInterval(interval);
          return;
        }

        if (currentIndex >= text.length) {
          clearInterval(interval);
          if (onComplete && isMountedRef.current) onComplete();
          return;
        }

        const partialText = text.substring(0, currentIndex + 1);
        if (isMountedRef.current) onUpdate(partialText);
        currentIndex++;
      }, charDelay);

      timeoutRefs.current.push(interval);
      return interval;
    }
  };

  // Event-driven creep: smooth ease-out progress toward 90%
  const startEventDrivenCreep = () => {
    if (creepIntervalRef.current) {
      clearInterval(creepIntervalRef.current);
    }
    const maxTarget = 90; // COMPLETE will jump to 100%
    creepIntervalRef.current = setInterval(() => {
      if (!isAnimatingRef.current || !isMountedRef.current) {
        clearInterval(creepIntervalRef.current);
        creepIntervalRef.current = null;
        return;
      }
      const currentProgress = state.loadingProgress;
      if (currentProgress < maxTarget) {
        // Exponential ease-out: advance by 4% of remaining gap each tick
        // Gives smooth deceleration — fast at first, gentle near target
        const gap = maxTarget - currentProgress;
        const increment = Math.max(0.5, gap * 0.04);
        state.updateLoadingProgress(
          Math.min(Number.parseFloat((currentProgress + increment).toFixed(1)), maxTarget)
        );
      }
    }, 1000);
  };

  // Resume animation from saved progress (after remount).
  // Restarts timers without resetting savedState — preserves progress bar and sections.
  const resumeAnimation = () => {
    isAnimatingRef.current = true;

    // Event-driven mode: restart creep from saved progress
    if (searchProgress !== null) {
      startEventDrivenCreep();
      // Continue typing from next unfinished section
      const nextSection = Math.max(0, state.currentSection);
      typeSection(nextSection);
      return;
    }

    // Timer mode: restart progress bar from saved position
    runProgressTimer(state.loadingProgress);

    // Resume typing from correct position
    if (state.currentSection < 0 && state.localCriteria.length > 0) {
      // Criteria section was in progress when we unmounted
      startCriteriaSection();
    } else {
      typeSection(Math.max(0, state.currentSection));
    }
  };

  // Shared progress timer used by both fresh-start and resume paths.
  // initialProgress = 0 for fresh start, or savedState value for resume.
  // Computes elapsed-time offset so the phase table resumes correctly.
  const runProgressTimer = (initialProgress = 0) => {
    const loadingTime =
      state.localCriteria.length === 0
        ? state.localTotalPrintTime
        : state.localTotalPrintTime - state.localCriteria.length * 1000;

    if (loadingTime <= 0) {
      state.updateLoadingProgress(98);
      return;
    }

    // Compute elapsed offset so phase table picks up from the correct position.
    // Find the phase that brackets initialProgress and derive the time fraction.
    let elapsedOffset = 0;
    if (initialProgress > 0) {
      const bracketPhase = PROGRESS_PHASES.find(
        (p) => initialProgress >= p.startProgress && initialProgress < p.endProgress
      );
      if (bracketPhase) {
        const progressFraction =
          (initialProgress - bracketPhase.startProgress) /
          (bracketPhase.endProgress - bracketPhase.startProgress);
        const timeFraction =
          bracketPhase.timeStart +
          progressFraction * (bracketPhase.timeEnd - bracketPhase.timeStart);
        elapsedOffset = timeFraction * loadingTime;
      }
    }

    const startTime = Date.now() - elapsedOffset;
    let currentProgress = initialProgress;
    let lastUpdateTime = Date.now();

    intervalRef.current = setInterval(() => {
      if (!isAnimatingRef.current || !isMountedRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        return;
      }

      const elapsed = Date.now() - startTime;
      const timeProgress = elapsed / loadingTime;
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTime;

      const currentPhase = PROGRESS_PHASES.find(
        (phase) => timeProgress >= phase.timeStart && timeProgress < phase.timeEnd
      );

      if (currentPhase) {
        const phaseTimeProgress =
          (timeProgress - currentPhase.timeStart) / (currentPhase.timeEnd - currentPhase.timeStart);
        const phaseTargetProgress =
          currentPhase.startProgress +
          phaseTimeProgress * (currentPhase.endProgress - currentPhase.startProgress);

        if (timeSinceLastUpdate >= currentPhase.delay && currentProgress < phaseTargetProgress) {
          if (currentPhase.increment >= 9) {
            currentProgress = Math.min(
              currentProgress + currentPhase.increment,
              currentPhase.endProgress,
              phaseTargetProgress
            );
          } else {
            currentProgress = Math.min(
              currentProgress + currentPhase.increment,
              phaseTargetProgress
            );
          }

          state.updateLoadingProgress(currentProgress);
          lastUpdateTime = now;
        }
      }

      if (timeProgress >= 1) {
        state.updateLoadingProgress(98);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 50);
  };

  const startAnimation = () => {
    if (state.animationStarted) return;

    clearAllAnimations();
    isAnimatingRef.current = true;

    state.resetState();
    state.updateAnimationStarted(true);

    // Event-driven mode: apply current progress immediately, then creep between events
    if (searchProgress !== null) {
      state.updateCurrentStep('loading');
      // Apply the current searchProgress value immediately (it's already set by the time
      // startAnimation runs, since INITIATING+ANALYZING are emitted synchronously before
      // the component renders, but startAnimation is delayed 100ms via setTimeout)
      const initialTarget = searchProgress.targetProgress ?? 15;
      state.updateLoadingProgress(initialTarget);
      startEventDrivenCreep();
      typeSection(0);
      return;
    }

    // Timer mode (existing behavior)
    if (state.localCriteria.length > 0) {
      startCriteriaSection();
    } else {
      startProgressBar();
      typeSection(0);
    }
  };

  const startCriteriaSection = () => {
    const criteriaSection = state.allSections[0];
    const criteriaTime = state.localCriteria.length * 1000;
    const titleTime = criteriaTime * 0.3;
    const contentTime = criteriaTime * 0.6;

    state.updateCurrentSection(0);
    state.updateCurrentStep('loading');

    typeText(
      criteriaSection.title,
      (partialText) => {
        state.updatePrintedSectionTitles(0, partialText);
      },
      () => {
        if (!isAnimatingRef.current) return;
        typeText(
          criteriaSection.content,
          (partialText) => {
            state.updatePrintedSectionContent(0, partialText);
          },
          () => {
            if (!isAnimatingRef.current) return;
            startProgressBar();
            const timeout = setTimeout(() => {
              if (isAnimatingRef.current) {
                typeSection(1);
              }
            }, criteriaTime * 0.05);
            timeoutRefs.current.push(timeout);
          },
          contentTime
        );
      },
      titleTime
    );
  };

  const startProgressBar = () => {
    runProgressTimer(0);
  };

  const getSectionTime = (section) => {
    if (searchProgress !== null) {
      // Event-driven mode: fixed ~30ms/char typing speed
      const titleLen = typeof section.title === 'string' ? section.title.length : 0;
      let contentLen = 0;
      if (typeof section.content === 'string') {
        contentLen = section.content.length;
      } else if (Array.isArray(section.content)) {
        contentLen = section.content.reduce(
          (acc, item) => acc + (typeof item === 'string' ? item.length : 0),
          0
        );
      }
      return (titleLen + contentLen) * 30;
    }
    const loadingSectionCount = state.localWithRationale ? 5 : 4;
    if (state.localCriteria.length === 0) {
      return state.localTotalPrintTime / loadingSectionCount;
    }
    const criteriaTime = state.localCriteria.length * 1000;
    if (section.isCriteria) return criteriaTime;
    return (state.localTotalPrintTime - criteriaTime) / loadingSectionCount;
  };

  const typeSection = (sectionIndex) => {
    if (!isAnimatingRef.current) return;

    if (sectionIndex >= state.allSections.length) {
      // In event mode, don't override progress or stop animation —
      // the creep timer and COMPLETE phase handler control the bar
      if (creepIntervalRef.current) {
        return;
      }
      state.updateCurrentStep('complete');
      state.updateLoadingProgress(98);
      isAnimatingRef.current = false;
      return;
    }

    state.updateCurrentSection(sectionIndex);
    state.updateCurrentStep('loading');

    const section = state.allSections[sectionIndex];
    const sectionTime = getSectionTime(section);
    const titleTime = sectionTime * 0.3;
    const contentTime = sectionTime * 0.6;

    typeText(
      section.title,
      (partialText) => {
        state.updatePrintedSectionTitles(sectionIndex, partialText);
      },
      () => {
        if (!isAnimatingRef.current) return;
        typeText(
          section.content,
          (partialText) => {
            state.updatePrintedSectionContent(sectionIndex, partialText);
          },
          () => {
            if (!isAnimatingRef.current) return;
            const timeout = setTimeout(() => {
              if (isAnimatingRef.current) {
                typeSection(sectionIndex + 1);
              }
            }, sectionTime * 0.05);
            timeoutRefs.current.push(timeout);
          },
          contentTime
        );
      },
      titleTime
    );
  };

  // Shared resume-or-start logic used by both main effect and keyRender effect.
  // Returns the timer ID for cleanup.
  const scheduleAnimationStart = () => {
    const isResume =
      state.localSearchId &&
      savedState.hasCalculations(state.localSearchId) &&
      savedState.getCalculations(state.localSearchId).animationStarted;

    if (isResume) {
      clearAllAnimations();
      isAnimatingRef.current = true;
      return setTimeout(() => {
        resumeAnimation();
      }, 100);
    }

    clearAllAnimations();
    if (state.localSearchId) {
      savedState.saveCalculations(state.localSearchId, {});
    }
    state.resetState();

    return setTimeout(() => {
      startAnimation();
    }, 100);
  };

  // Update searchId
  useEffect(() => {
    if (state.localSearchId !== searchId) {
      runInAction(() => {
        state.localSearchId = searchId;
      });
    }
  }, [searchId]);

  // THIS IS THE MAIN EFFECT - handles ALL prop changes
  useEffect(() => {
    // Update all props in state
    runInAction(() => {
      state.localCriteria = criteria;
      state.localTotalPrintTime = totalPrintTime;
      state.localWithRationale = withRationale;
      state.localIsComparables = isComparables;
    });

    // Update withRationale state
    if (state.localSearchId) {
      state.updateSavedState({ withRationaleState: state.localWithRationale });
    } else {
      runInAction(() => {
        state.localWithRationaleState = state.localWithRationale;
      });
    }

    const timer = start ? scheduleAnimationStart() : null;

    // ALWAYS return cleanup to clear animations when effect re-runs or unmounts
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      clearAllAnimations();
    };
  }, [
    JSON.stringify(criteria),
    withRationale,
    isComparables,
    totalPrintTime,
    start,
    state.localSearchId
  ]);

  // Event-driven mode: react to searchProgress phase changes (progress bar + creep only)
  // Sections auto-advance via typeSection timer — no need to force-jump sections here
  useEffect(() => {
    if (!searchProgress || !isMountedRef.current) return;

    const { phase } = searchProgress;

    // On COMPLETE, stop creep and show 100% (no isAnimatingRef guard — fast searches
    // may complete before the 100ms animation start delay)
    if (phase === 'COMPLETE') {
      if (creepIntervalRef.current) {
        clearInterval(creepIntervalRef.current);
        creepIntervalRef.current = null;
      }
      state.updateLoadingProgress(100);
      state.updateCurrentStep('complete');
      return;
    }

    // Non-COMPLETE phases only apply once animation has started
    if (!isAnimatingRef.current) return;
    // Creep handles progress smoothly, no jump needed
  }, [searchProgress?.phase, searchProgress?.targetProgress]);

  // keyRender handling
  useEffect(() => {
    const timer = keyRender !== null && start ? scheduleAnimationStart() : null;

    // ALWAYS return cleanup
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      clearAllAnimations();
    };
  }, [keyRender, start]);

  return {
    componentProps: {
      currentStep: state.currentStep,
      loadingProgress: state.loadingProgress,
      currentSection: state.currentSection,
      animationStarted: state.animationStarted,
      allSections: state.allSections,
      printedSectionTitles: state.printedSectionTitles,
      printedSectionContent: state.printedSectionContent,
      isAnimating: isAnimatingRef.current,
      searchId: state.localSearchId,
      disableAutoScroll
    },
    currentStep: state.currentStep,
    loadingProgress: state.loadingProgress,
    currentSection: state.currentSection,
    animationStarted: state.animationStarted,
    isAnimating: isAnimatingRef.current,
    cleanup: clearAllAnimations
  };
};

// Component
const ProfilerSearchResultsLoader = observer(
  ({ loaderClassname = '', wrapClassname = '', componentProps }) => {
    const {
      currentStep,
      loadingProgress,
      currentSection,
      allSections,
      printedSectionTitles,
      printedSectionContent,
      isAnimating,
      disableAutoScroll
    } = componentProps;

    const containerRef = useRef(null);
    const endOfContentRef = useRef(null);
    const isUserScrolling = useRef(false);
    const scrollTimeout = useRef(null);

    // Effect to detect user's manual scroll input
    useEffect(() => {
      const handleManualScroll = () => {
        isUserScrolling.current = true;
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }
        scrollTimeout.current = setTimeout(() => {
          isUserScrolling.current = false;
        }, 200); // User is considered "done" scrolling after 200ms of inactivity
      };

      window.addEventListener('wheel', handleManualScroll, { passive: true });
      window.addEventListener('touchstart', handleManualScroll, { passive: true });

      return () => {
        window.removeEventListener('wheel', handleManualScroll);
        window.removeEventListener('touchstart', handleManualScroll);
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }
      };
    }, []);

    // This is the primary effect for handling auto-scrolling.
    useEffect(() => {
      // Pause auto-scroll if disabled, user is manually scrolling, or animation is off.
      if (disableAutoScroll || isUserScrolling.current || !isAnimating) {
        return;
      }

      const currentSectionContainer = containerRef.current?.querySelector(
        `[data-section-index="${currentSection}"]`
      );

      if (!currentSectionContainer) {
        return;
      }

      let elementToWatch = currentSectionContainer.querySelector('.psrln-text:not(.section-title)');
      if (!elementToWatch) {
        elementToWatch = currentSectionContainer;
      }

      const viewportHeight = window.innerHeight;
      const { bottom: elementBottom } = elementToWatch.getBoundingClientRect();
      const scrollThreshold = 75;
      const shouldScroll = elementBottom <= viewportHeight + scrollThreshold;

      if (shouldScroll && endOfContentRef.current) {
        endOfContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, [
      disableAutoScroll,
      isAnimating,
      currentSection,
      JSON.stringify(printedSectionTitles),
      JSON.stringify(printedSectionContent)
    ]);

    const renderContent = (text) => {
      if (!text) return null;
      return text.split('\n').map((line, index) => (
        <Fragment key={index}>
          {line}
          {index < text.split('\n').length - 1 && <br />}
        </Fragment>
      ));
    };

    return (
      <div className="profiler-search-results-loader-new-wrapper">
        <div
          className={`profiler-search-results-loader-new-wrap ${wrapClassname}`.trim()}
          ref={containerRef}
        >
          <div className="psrln-spacer-m" />
          <div className={`psrln-actual-loader-wrapper ${loaderClassname}`}>
            <div className="psrln-actual-loader-wrap">
              <div
                className="psrln-loader-width"
                style={{
                  width: `max(20px, ${loadingProgress}%)`,
                  background: `linear-gradient(90deg, #348cf9 0%, #4f9aff 50%, #6bb3ff 100%)`,
                  transition: 'width 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  boxShadow: loadingProgress > 0 ? '0 0 8px rgba(52, 140, 249, 0.4)' : 'none',
                  transform: loadingProgress > 0 ? 'scaleY(1.05)' : 'scaleY(1)',
                  transformOrigin: 'left center'
                }}
              >
                <div className="psrln-loader-percentage-txt">
                  {loadingProgress === 0 ? (
                    <Oval
                      visible={true}
                      height="10"
                      width="10"
                      color="#ffffff"
                      secondaryColor="#cde4fa"
                      ariaLabel="oval-loading"
                      wrapperStyle={{}}
                      wrapperClass="psrln-loader-percentage-loader"
                    />
                  ) : (
                    `${Math.round(loadingProgress)}%`
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="psrln-spacer-m" />
          <div className="psrln-text agentic-title">Agentic AI</div>
          <div className="psrln-spacer-m" />
          {allSections.map((section, index) => {
            if (currentSection < index) return null;
            return (
              <div key={section.id} data-section-index={index}>
                <div className="psrln-text section-title">
                  <div className="section-icon-wrap">
                    <img src={section.icon} alt="" />
                  </div>
                  {printedSectionTitles[index] || ''}
                  {/* Show cursor when typing title */}
                  {currentSection === index &&
                    printedSectionTitles[index] &&
                    !printedSectionContent[index] &&
                    currentStep === 'loading' && <span className="typing-cursor">|</span>}
                </div>
                {printedSectionTitles[index] && (
                  <>
                    <div className="psrln-spacer-sm" />
                    <div className="psrln-text" style={{ position: 'relative' }}>
                      {section.isCriteria
                        ? printedSectionContent[index] &&
                          printedSectionContent[index].split('\n').map((line, lineIndex) => {
                            if (!line.trim()) return null;
                            const parts = line.split(': ');
                            const isLastLine =
                              lineIndex ===
                              printedSectionContent[index].split('\n').filter((l) => l.trim())
                                .length -
                                1;
                            const isCurrentlyTyping =
                              currentSection === index && currentStep === 'loading';

                            return (
                              <Fragment key={lineIndex}>
                                <b>{parts[0]}</b>
                                {parts.length > 1 ? ': ' + parts.slice(1).join(': ') : ''}
                                {/* Show cursor at the end of the last line being typed */}
                                {isLastLine && isCurrentlyTyping && (
                                  <span className="typing-cursor">|</span>
                                )}
                                <br />
                              </Fragment>
                            );
                          })
                        : Array.isArray(printedSectionContent[index])
                          ? printedSectionContent[index].map((item, itemIndex) => {
                              if (!item) return null;
                              if (typeof item === 'string') {
                                return <span key={itemIndex}>{item}</span>;
                              }
                              if (item.type === 'img') {
                                return <img key={itemIndex} {...item.props} alt="" />;
                              }
                              if (item.type === 'br') {
                                return <br key={itemIndex} />;
                              }
                              return null;
                            })
                          : renderContent(printedSectionContent[index])}

                      {/* Show cursor when typing content (non-criteria sections) */}
                      {!section.isCriteria &&
                        currentSection === index &&
                        printedSectionContent[index] &&
                        printedSectionTitles[index] &&
                        currentStep === 'loading' && <span className="typing-cursor">|</span>}
                    </div>
                    <div className="psrln-spacer-m" />
                  </>
                )}
              </div>
            );
          })}
          {/* This empty div acts as a reliable anchor for our auto-scrolling */}
          <div ref={endOfContentRef} />
        </div>
      </div>
    );
  }
);

// Test-only export for verifying savedState persistence across mounts.
// Production builds tree-shake unused exports, so no guard needed.
export const _savedStateForTesting = savedState;

export default ProfilerSearchResultsLoader;
